import {
  useIsFeatureActive,
  useIsSoftFeatureToggleActive,
} from '@aboutyou/feature-flags';
import {useRouter} from '@aboutyou/legacy-desktop/src/app/helpers/navigation';
import {PageTrackingContextProvider} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/PageTrackingContext';
import {useTracking} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackingContextAndClientV6';
import {useTrackingPageUrl} from '@aboutyou/legacy-desktop/src/app/helpers/tracking/hooks/useTrackingPageUrl';
import TrackContentPageView from '@aboutyou/legacy-desktop/src/app/helpers/tracking/trackerComponents/TrackContentPageView';
import redialHandle from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/redialHelpCenterArticlePage';
import {
  useHelpCenterAlternateLink,
  useHelpCenterArticlePageCanonicalUrl,
} from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/seo';
import useHelpCenterTranslations from '@aboutyou/legacy-desktop/src/app/pages/HelpCenter/useHelpCenterTranslations';
import {useService} from '@aboutyou/legacy-desktop/src/app/services/utils/context';
import {useHelpCenterArticlePageStore} from '@aboutyou/legacy-desktop/src/app/store/context';
import {useLocale} from '@aboutyou/legacy-desktop/src/app/store/features/locale/useLocale';
import LegacyPage from '@aboutyou/legacy-shared/src/pages/HelpCenter/HelpCenterArticlePage';
import {TrackingPageTypeWithId} from '@aboutyou/legacy-tracking-sdk';
import {CloudflareTurnstile} from '@aboutyou/logic-components';
import {useShowToast} from '@aboutyou/ui-components';
import React from 'react';
import redial from 'redial';
import {withLegacyProviders} from '../../LegacyComponentWrapper';

const ConnectedHelpCenterArticlePage: React.FC<
  React.PropsWithChildren
> = () => {
  const {TrackingClient} = useTracking();
  const {navigateClient} = useRouter();
  const showToast = useShowToast();

  const activeArticle = useHelpCenterArticlePageStore(
    store => store.activeArticle,
  );
  const translations = useHelpCenterTranslations({
    activeArticleName: activeArticle?.name,
  });
  const sections = useHelpCenterArticlePageStore(store => store.sections ?? []);
  const articleId = useHelpCenterArticlePageStore(store => store.articleId);
  const activeSectionId = useHelpCenterArticlePageStore(
    store => store.activeSectionId,
  );
  const relatedArticles = useHelpCenterArticlePageStore(
    store => store.relatedArticles ?? [],
  );
  const canonical = useHelpCenterArticlePageCanonicalUrl();
  const {path, search} = useTrackingPageUrl();
  const alternateLink = useHelpCenterAlternateLink(canonical);
  const {helpCenter} = useService();
  const {locale} = useLocale();

  const isShowOldHelpCenterEnabled =
    useIsSoftFeatureToggleActive('show_old_help_center') === true;

  const isCoinsActive = useIsFeatureActive('ff_coins_rollout').isActive;

  return (
    <PageTrackingContextProvider
      id={articleId}
      path={path}
      search={search}
      type={TrackingPageTypeWithId.HelpCenterArticle}
    >
      <TrackContentPageView
        pageTitle={translations.articlePage.seo_page_title}
      />
      <LegacyPage
        locale={locale}
        endpoints={helpCenter}
        activeSectionId={activeSectionId}
        canonicalUrl={canonical}
        trackingClient={TrackingClient}
        translations={translations}
        navigateClient={navigateClient}
        activeArticle={activeArticle}
        relatedArticles={relatedArticles}
        sections={sections}
        alternateLink={alternateLink}
        isCoinsActive={isCoinsActive}
        isShowOldHelpCenterEnabled={isShowOldHelpCenterEnabled}
        CloudflareTurnstile={CloudflareTurnstile}
        isDesktop
        showToast={showToast}
      />
    </PageTrackingContextProvider>
  );
};

const PageWithRedial = redial.provideHooks(redialHandle)(
  ConnectedHelpCenterArticlePage,
);

export const ArticlePage = withLegacyProviders(PageWithRedial);
